import { atom } from "recoil";
import { TraceDataConfig } from "../Types/Trace";
import {
    Analytic,
    COxAnalysis,
    CPPOptAnalysis,
    CPPOptFlexAnalysis,
    DEFAULT_DSA_DISPLAY_SCALE,
    DEFAULT_QEEG_DSA_CHANNEL_GROUP_ID,
    MAPOptAnalysis,
    MAPOptFlexAnalysis,
    PRxAnalysis,
    QEEGDSAAnalysis,
    QEEGDSAChannelGroupId,
} from "../Types/AnalysisDetails";
import { DataSource } from "../Types/DataSource";
import { getSpectrogramDataKey } from "../Types/Trace";

const defaultPRxConfig: PRxAnalysis = {
    analytic: Analytic.PRx,
    calculationPeriodMs: 60 * 1000,
    calculationWindowMs: 5 * 60 * 1000,
    ABP: "ABP_na",
    ICP: "ICP_na"
}

const defaultCOxConfig: COxAnalysis = {
    analytic: Analytic.COx,
    calculationPeriodMs: 60 * 1000,
    calculationWindowMs: 5 * 60 * 1000,
    StO2Left: "StO2_1_Left_Brain",
    StO2Right: "StO2_2_Right_Brain",
    enableLeft: true,
    enableRight: true,
    ABP: "ABP_na"
}

const defaultCPPOptConfig: CPPOptAnalysis = {
    analytic: Analytic.CPPOpt,
    ABP: "ABP_na",
    ICP: "ICP_na",
    prxThreshold: 0.25,
    calculationWindowMs: 4 * 60 * 60 * 1000,
    calculationPeriodMs: 60 * 1000
}

const defaultCPPOptFlexConfig: CPPOptFlexAnalysis = {
    analytic: Analytic.CPPOpt_FLEX,
    ABP: "ABP_na",
    ICP: "ICP_na",
    prxThreshold: 0.25,
    calculationWindowMs: 8 * 60 * 60 * 1000,
    calculationPeriodMs: 60 * 1000
}

const defaultMAPOptConfig: MAPOptAnalysis = {
    analytic: Analytic.MAPOpt,
    ABP: "ABP_na",
    stO2Left: "StO2_1_Left_Brain",
    stO2Right: "StO2_2_Right_Brain",
    enableLeft: true,
    enableRight: true,
    coxThreshold: 0.3,
    calculationWindowMs: 4 * 60 * 60 * 1000,
    calculationPeriodMs: 60 * 1000
}


const defaultMAPOptFlexConfig: MAPOptFlexAnalysis = {
    analytic: Analytic.MAPOpt_FLEX,
    ABP: "ABP_na",
    stO2Left: "StO2_1_Left_Brain",
    stO2Right: "StO2_2_Right_Brain",
    enableLeft: true,
    enableRight: true,
    coxThreshold: 0.3,
    calculationWindowMs: 8 * 60 * 60 * 1000,
    calculationPeriodMs: 60 * 1000
}

const getDefaultQEEGDSAConfig = (
    channelGroupId: QEEGDSAChannelGroupId = DEFAULT_QEEG_DSA_CHANNEL_GROUP_ID
): QEEGDSAAnalysis => ({
    analytic: Analytic.QEEG_DSA,
    channelGroupId,
    displayScale: DEFAULT_DSA_DISPLAY_SCALE,
})

const getQEEGDSATrace = (): TraceDataConfig => {
    const onDemandAnalysis = getDefaultQEEGDSAConfig()

    return {
        id: `${Math.random()}-${new Date(Date.now()).toISOString()}`,
        name: "qEEG DSA",
        rawDataKey: "qEEG_DSA",
        units: "",
        dataKey: getSpectrogramDataKey("qEEG_DSA", onDemandAnalysis),
        dataSource: DataSource.CURRENT_PATIENT,
        isCompositePart: false,
        onDemandAnalysis,
    }
}

export const analyticTracesAtom = atom<TraceDataConfig[]>({
    key: "analytics",
    default: [
        {
            id: `${Math.random()}-${new Date(Date.now()).toISOString()}`, // It is okay if the ID changes, as long as it is always unique.
            units: "",
            name: "PRx",
            rawDataKey: "PRx",
            dataKey: JSON.stringify(defaultPRxConfig),
            isCompositePart: false,
            dataSource: DataSource.CURRENT_PATIENT,
            onDemandAnalysis: defaultPRxConfig,
        },
        {
            id: `${Math.random()}-${new Date(Date.now()).toISOString()}`,
            units: "",
            name: "COx",
            rawDataKey: "COx",
            dataKey: JSON.stringify(defaultCOxConfig),
            isCompositePart: false,
            dataSource: DataSource.CURRENT_PATIENT,
            onDemandAnalysis: defaultCOxConfig,
        },
        {
            id: `${Math.random()}-${new Date(Date.now()).toISOString()}`,
            name: "CPPOpt",
            rawDataKey: "CPPOpt",
            units: "mmHg",
            dataKey: JSON.stringify(defaultCPPOptConfig),
            dataSource: DataSource.CURRENT_PATIENT,
            isCompositePart: true,
            compositeIndex: 0,
            onDemandAnalysis: defaultCPPOptConfig
        },
        {
            id: `${Math.random()}-${new Date(Date.now()).toISOString()}`,
            name: "CPPOpt_LLA",
            rawDataKey: "CPPOpt_LLA",
            units: "mmHg",
            dataKey: JSON.stringify(defaultCPPOptConfig),
            dataSource: DataSource.CURRENT_PATIENT,
            isCompositePart: true,
            compositeIndex: 1,
            onDemandAnalysis: defaultCPPOptConfig,
        },
        {
            id: `${Math.random()}-${new Date(Date.now()).toISOString()}`,
            name: "CPPOpt_ULA",
            rawDataKey: "CPPOpt_ULA",
            units: "mmHg",
            dataKey: JSON.stringify(defaultCPPOptConfig),
            dataSource: DataSource.CURRENT_PATIENT,
            isCompositePart: true,
            compositeIndex: 2,
            onDemandAnalysis: defaultCPPOptConfig
        },
        {
            id: `${Math.random()}-${new Date(Date.now()).toISOString()}`,
            name: "CPPOpt (multi-window)",
            rawDataKey: "CPPOpt (multi-window)",
            units: "mmHg",
            dataKey: JSON.stringify(defaultCPPOptFlexConfig),
            dataSource: DataSource.CURRENT_PATIENT,
            isCompositePart: true,
            compositeIndex: 0,
            onDemandAnalysis: defaultCPPOptFlexConfig
        },
        {
            id: `${Math.random()}-${new Date(Date.now()).toISOString()}`,
            name: "CPPOpt_LLA (multi-window)",
            rawDataKey: "CPPOpt_LLA (multi-window)",
            units: "mmHg",
            dataKey: JSON.stringify(defaultCPPOptFlexConfig),
            dataSource: DataSource.CURRENT_PATIENT,
            isCompositePart: true,
            compositeIndex: 1,
            onDemandAnalysis: defaultCPPOptFlexConfig,
        },
        {
            id: `${Math.random()}-${new Date(Date.now()).toISOString()}`,
            name: "CPPOpt_ULA (multi-window)",
            rawDataKey: "CPPOpt_ULA (multi-window)",
            units: "mmHg",
            dataKey: JSON.stringify(defaultCPPOptFlexConfig),
            dataSource: DataSource.CURRENT_PATIENT,
            isCompositePart: true,
            compositeIndex: 2,
            onDemandAnalysis: defaultCPPOptFlexConfig
        },
        {
            id: `${Math.random()}-${new Date(Date.now()).toISOString()}`,
            name: "MAPOpt",
            rawDataKey: "MAPOpt",
            units: "mmHg",
            dataKey: JSON.stringify(defaultMAPOptConfig),
            dataSource: DataSource.CURRENT_PATIENT,
            isCompositePart: true,
            compositeIndex: 0,
            onDemandAnalysis: defaultMAPOptConfig
        },
        {
            id: `${Math.random()}-${new Date(Date.now()).toISOString()}`,
            name: "MAPOpt_LLA",
            rawDataKey: "MAPOpt_LLA",
            units: "mmHg",
            dataKey: JSON.stringify(defaultMAPOptConfig),
            dataSource: DataSource.CURRENT_PATIENT,
            isCompositePart: true,
            compositeIndex: 1,
            onDemandAnalysis: defaultMAPOptConfig
        },
        {
            id: `${Math.random()}-${new Date(Date.now()).toISOString()}`,
            name: "MAPOpt_ULA",
            rawDataKey: "MAPOpt_ULA",
            units: "mmHg",
            dataKey: JSON.stringify(defaultMAPOptConfig),
            dataSource: DataSource.CURRENT_PATIENT,
            isCompositePart: true,
            compositeIndex: 2,
            onDemandAnalysis: defaultMAPOptConfig
        },
        {
            id: `${Math.random()}-${new Date(Date.now()).toISOString()}`,
            name: "MAPOpt (multi-window)",
            rawDataKey: "MAPOpt (multi-window)",
            units: "mmHg",
            dataKey: JSON.stringify(defaultMAPOptFlexConfig),
            dataSource: DataSource.CURRENT_PATIENT,
            isCompositePart: true,
            compositeIndex: 0,
            onDemandAnalysis: defaultMAPOptFlexConfig
        },
        {
            id: `${Math.random()}-${new Date(Date.now()).toISOString()}`,
            name: "MAPOpt_LLA (multi-window)",
            rawDataKey: "MAPOpt_LLA (multi-window)",
            units: "mmHg",
            dataKey: JSON.stringify(defaultMAPOptFlexConfig),
            dataSource: DataSource.CURRENT_PATIENT,
            isCompositePart: true,
            compositeIndex: 1,
            onDemandAnalysis: defaultMAPOptFlexConfig
        },
        {
            id: `${Math.random()}-${new Date(Date.now()).toISOString()}`,
            name: "MAPOpt_ULA (multi-window)",
            rawDataKey: "MAPOpt_ULA (multi-window)",
            units: "mmHg",
            dataKey: JSON.stringify(defaultMAPOptFlexConfig),
            dataSource: DataSource.CURRENT_PATIENT,
            isCompositePart: true,
            compositeIndex: 2,
            onDemandAnalysis: defaultMAPOptFlexConfig
        },
        getQEEGDSATrace(),
    ]
})
